import React, { useState, useEffect } from 'react';
import { AdAsset, AdSettings, DEFAULT_AD_SETTINGS, parseYouTubeUrl } from '../types';
import AdTicker from './AdTicker';
import { HeartPulse } from 'lucide-react';

interface AdFullscreenProps {
    assets: AdAsset[];
    adSettings: AdSettings;
    hospitalName: string;
    hospitalLogo?: string;
    hospitalLogoDark?: string;
    primaryColor?: string;
    theme?: 'dark' | 'light';
    tickerAssets?: AdAsset[];
    hospitalId?: string;
}

const AdFullscreen: React.FC<AdFullscreenProps> = ({
    assets,
    adSettings = DEFAULT_AD_SETTINGS,
    hospitalName,
    hospitalLogo,
    hospitalLogoDark,
    primaryColor = '#5B65DC',
    theme = 'dark',
    tickerAssets = [],
    hospitalId,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());

    const slides = assets
        .filter(a => a.isActive && a.type === 'FULLSCREEN_SLIDE')
        .filter(a => a.target === 'ALL' || a.target === 'TV_ONLY')
        .sort((a, b) => a.priority - b.priority);

    // Also include BANNER_IMAGE assets as slides when in fullscreen mode
    const bannerImages = assets
        .filter(a => a.isActive && a.type === 'BANNER_IMAGE')
        .filter(a => a.target === 'ALL' || a.target === 'TV_ONLY')
        .sort((a, b) => a.priority - b.priority);

    const allSlides = [...slides, ...bannerImages];

    // Clock
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Slide rotation
    useEffect(() => {
        if (allSlides.length <= 1) return;

        const interval = (adSettings.fullscreenIntervalSec || 10) * 1000;

        const timer = setTimeout(() => {
            setIsTransitioning(true);
            setTimeout(() => {
                setCurrentIndex((prev) => (prev + 1) % allSlides.length);
                setIsTransitioning(false);
            }, 600);
        }, interval);

        return () => clearTimeout(timer);
    }, [allSlides.length, currentIndex, adSettings.fullscreenIntervalSec]);

    const isDark = theme === 'dark';
    const currentSlide = allSlides.length > 0 ? allSlides[currentIndex % allSlides.length] : null;
    const logoSrc = isDark && hospitalLogoDark ? hospitalLogoDark : hospitalLogo;

    return (
        <div className={`h-full w-full flex flex-col ${isDark ? 'bg-[#0B1121]' : 'bg-slate-50'}`}>
            {/* Main content area */}
            <div className="flex-1 relative flex items-center justify-center overflow-hidden">
                {/* Background gradient */}
                <div
                    className="absolute inset-0 opacity-20"
                    style={{
                        background: `radial-gradient(ellipse at center, ${primaryColor}30, transparent 70%)`,
                    }}
                />

                {currentSlide && (currentSlide.imageUrl || currentSlide.youtubeUrl) ? (
                    /* Fullscreen image, video, or YouTube slide */
                    <div
                        className={`absolute inset-0 flex items-center justify-center transition-all duration-600 ${isTransitioning ? 'opacity-0 scale-[1.02]' : 'opacity-100 scale-100'
                            }`}
                    >
                        {currentSlide.youtubeUrl && parseYouTubeUrl(currentSlide.youtubeUrl) ? (
                            <iframe
                                key={'yt-' + currentSlide.id + '-' + (currentIndex % allSlides.length)}
                                src={parseYouTubeUrl(currentSlide.youtubeUrl)!}
                                className="w-full h-full border-0"
                                allow="autoplay; encrypted-media"
                                allowFullScreen
                                title="YouTube"
                            />
                        ) : currentSlide.mediaType === 'video' ? (
                            <video
                                key={currentSlide.id + '-' + (currentIndex % allSlides.length)}
                                src={currentSlide.imageUrl}
                                autoPlay
                                muted
                                playsInline
                                className="max-w-full max-h-full object-contain"
                                onEnded={() => {
                                    if (allSlides.length > 1) {
                                        setIsTransitioning(true);
                                        setTimeout(() => {
                                            setCurrentIndex((prev) => (prev + 1) % allSlides.length);
                                            setIsTransitioning(false);
                                        }, 600);
                                    }
                                }}
                            />
                        ) : (
                            <img
                                src={currentSlide.imageUrl}
                                alt={currentSlide.imageAlt || 'Publicidad'}
                                className="max-w-full max-h-full object-contain"
                            />
                        )}
                    </div>
                ) : (
                    /* Fallback: Hospital branding + messaging */
                    <div className="flex flex-col items-center justify-center gap-8 relative z-10 animate-fade-in">
                        {logoSrc ? (
                            <div
                                className="p-6 rounded-3xl backdrop-blur-md"
                                style={{
                                    background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
                                    boxShadow: `0 0 60px ${primaryColor}20`,
                                }}
                            >
                                <img
                                    src={logoSrc}
                                    alt={hospitalName}
                                    className="h-24 md:h-32 object-contain filter drop-shadow-2xl"
                                />
                            </div>
                        ) : (
                            <div
                                className="w-24 h-24 md:w-32 md:h-32 rounded-3xl flex items-center justify-center shadow-2xl"
                                style={{
                                    backgroundColor: primaryColor,
                                    boxShadow: `0 0 80px ${primaryColor}60`,
                                }}
                            >
                                <HeartPulse className="w-12 h-12 md:w-16 md:h-16 text-white" />
                            </div>
                        )}

                        <h2
                            className={`text-3xl md:text-5xl font-black tracking-tight font-heading ${isDark ? 'text-white' : 'text-slate-900'
                                }`}
                            style={{ textShadow: isDark ? `0 0 40px ${primaryColor}30` : 'none' }}
                        >
                            {hospitalName}
                        </h2>

                        <p
                            className={`text-sm md:text-lg font-medium uppercase tracking-[0.3em] ${isDark ? 'text-white/40' : 'text-slate-400'
                                }`}
                        >
                            Sistema de seguimiento quirúrgico
                        </p>
                    </div>
                )}

                {/* Hospital logo overlay (top-left when showing slides, hidden for YouTube) */}
                {currentSlide && (currentSlide.imageUrl || currentSlide.youtubeUrl) && !currentSlide.youtubeUrl && logoSrc && (
                    <div className="absolute top-6 left-6 z-20">
                        <div
                            className="p-3 rounded-xl backdrop-blur-md"
                            style={{
                                background: isDark ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.8)',
                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
                            }}
                        >
                            <img src={logoSrc} alt="" className="h-8 object-contain" />
                        </div>
                    </div>
                )}

                {/* Clock overlay (top-right) */}
                <div className="absolute top-6 right-6 z-20">
                    <div
                        className={`px-6 py-3 rounded-2xl backdrop-blur-md font-mono font-black text-3xl ${isDark ? 'text-white/80' : 'text-slate-700'
                            }`}
                        style={{
                            background: isDark ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.7)',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
                        }}
                    >
                        {currentTime.toLocaleTimeString('es-MX', { hour12: false, hour: '2-digit', minute: '2-digit' })}
                    </div>
                </div>

                {/* Slide dots */}
                {allSlides.length > 1 && (
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                        {allSlides.map((_, i) => (
                            <div
                                key={i}
                                className={`h-2 rounded-full transition-all duration-500 ${i === currentIndex % allSlides.length ? 'w-8' : 'w-2 opacity-30'
                                    }`}
                                style={{
                                    backgroundColor: i === currentIndex % allSlides.length
                                        ? primaryColor
                                        : isDark ? '#ffffff' : '#64748b',
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Ticker at bottom */}
            {adSettings.tickerEnabled && tickerAssets.length > 0 && (
                <AdTicker
                    assets={tickerAssets}
                    speed={adSettings.tickerSpeed}
                    variant="tv"
                    theme={theme}
                    primaryColor={primaryColor}
                    hospitalId={hospitalId}
                />
            )}

            {/* Powered by footer */}
            <div
                className={`text-center py-1.5 shrink-0 ${isDark ? 'text-white/20' : 'text-slate-400'}`}
                style={{ fontSize: 'clamp(0.45rem, 0.5vw, 0.6rem)' }}
            >
                powered by <span className="font-semibold">hospital-status.com</span>
            </div>
        </div>
    );
};

export default AdFullscreen;
